import { recordPlatformLoginEventOnce } from '../features/superadmin/security/loginHistory.service'
import { supabase } from '../services/supabase'
import { signOut as authSignOut } from '../services/auth'
import type { PlatformAccountStatus } from '../features/superadmin/users/userStatus.types'
import { parsePlatformAccountStatus } from '../features/superadmin/users/userStatus.utils'

export class PlatformAccountBlockedError extends Error {
  readonly status: PlatformAccountStatus

  constructor (status: PlatformAccountStatus, reason: string | null) {
    const label = status === 'banned' ? 'geblokkeerd' : 'geschorst'
    const suffix = reason?.trim() ? ` Reden: ${reason.trim()}` : ''
    super(`Je account is ${label}.${suffix}`)
    this.name = 'PlatformAccountBlockedError'
    this.status = status
  }
}

const BLOCKED_ACCOUNT_MESSAGE_KEY = 'platform_account_blocked_message'

export function storePlatformAccountBlockedMessage (message: string): void {
  try {
    sessionStorage.setItem(BLOCKED_ACCOUNT_MESSAGE_KEY, message)
  } catch {
    // sessionStorage kan geblokkeerd zijn in private mode
  }
}

export function consumePlatformAccountBlockedMessage (): string | null {
  try {
    const message = sessionStorage.getItem(BLOCKED_ACCOUNT_MESSAGE_KEY)
    if (message) sessionStorage.removeItem(BLOCKED_ACCOUNT_MESSAGE_KEY)
    return message
  } catch {
    return null
  }
}

export function parseAccountStatusResponse (raw: unknown): {
  status: PlatformAccountStatus
  reason: string | null
} {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { status: 'active', reason: null }
  }
  const data = raw as Record<string, unknown>
  return {
    status: parsePlatformAccountStatus(data.status),
    reason: typeof data.reason === 'string' ? data.reason : null,
  }
}

/** Controleert of het huidige account toegang heeft; gooit bij schorsing/blokkade. */
export async function assertPlatformAccountAccess (): Promise<void> {
  const { data, error } = await supabase.rpc('get_my_platform_account_status')
  if (error) {
    if (import.meta.env.DEV) {
      console.debug('[get_my_platform_account_status]', error.message)
    }
    return
  }

  const { status, reason } = parseAccountStatusResponse(data)
  if (status === 'suspended' || status === 'banned') {
    const { data: { user } } = await supabase.auth.getUser()
    if (user) {
      await recordPlatformLoginEventOnce('login_blocked', { userId: user.id }).catch(() => {
        /* audit mag login-blokkade niet verhinderen */
      })
    }
    const blockedError = new PlatformAccountBlockedError(status, reason)
    storePlatformAccountBlockedMessage(blockedError.message)
    await authSignOut()
    throw blockedError
  }
}
